import React, { useEffect, useState, useMemo } from 'react';
import { Button, Card, Table, Space, Modal, Row, Col, Tooltip, Input, Tag, Form, Select, InputNumber, Tabs, Divider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, PlusCircleOutlined, SearchOutlined, FilterOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'underscore';

import { AppDispatch, CombinedStates } from '../../core/types';
import { SchoolClassroom } from '../../core/school/types';
import { getSchoolClassrooms, createOrUpdateSchoolClassroom } from '../../core/school/schoolApi';
import { loadSingleClassroom, resetSingleClassroom } from '../../core/school/schoolSlice';
import SchoolClassroomStatistic from '../SchoolClassroomStatistic';

import './style.scss';

const { Option } = Select;
const { TabPane } = Tabs;

interface SchoolClassroomsProps {
    schoolId: number;
}

// All classroom type options
const allClassroomTypeOptions = [
    { value: 13, label: 'Lopšelio grupė' },
    { value: 14, label: '3 metų amžiaus grupė' },
    { value: 15, label: '4 metų amžiaus grupė' },
    { value: 16, label: '5 metų amžiaus grupė' },
    { value: 17, label: 'Skirtingo amžiaus grupė' },
    { value: 18, label: 'Jungtinė IU grupė' },
    { value: 1, label: '1 klasė' },
    { value: 2, label: '2 klasė' },
    { value: 3, label: '3 klasė' },
    { value: 4, label: '4 klasė' },
    { value: 5, label: '5 klasė' },
    { value: 6, label: '6 klasė' },
    { value: 7, label: '7 klasė' },
    { value: 8, label: '8 klasė' },
    { value: 9, label: 'I gimnazijos klasė' },
    { value: 10, label: 'II gimnazijos klasė' },
    { value: 11, label: 'III gimnazijos klasė' },
    { value: 12, label: 'IV gimnazijos klasė' },
];

const SchoolClassrooms: React.FC<SchoolClassroomsProps> = ({ schoolId }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClassroomTypes, setSelectedClassroomTypes] = useState<number[]>([]);
    const [form] = Form.useForm();

    const {
        classrooms,
        isFetching,
        singleClassroom,
        singleSchool,
    } = useSelector((state: CombinedStates) => state.school);

    // Conditional classroom type options based on school type
    const classroomTypeOptions = useMemo(() => {
        if (singleSchool?.type === 'darzelis') {
            // For darzelis, show only options 13-18
            return allClassroomTypeOptions.filter(option => option.value >= 13 && option.value <= 18);
        }
        // For all other school types, show options 1-12
        return allClassroomTypeOptions.filter(option => option.value >= 1 && option.value <= 12);
    }, [singleSchool?.type]);

    useEffect(() => {
        dispatch(getSchoolClassrooms({ school_id: schoolId }));
    }, [dispatch, schoolId]);

    // Local filtering logic
    const filteredClassrooms = useMemo(() => {
        let filtered = [...classrooms];

        // Filter by search term
        if (searchTerm.trim()) {
            filtered = filtered.filter(classroom => classroom.title.toLowerCase().includes(searchTerm.toLowerCase()));
        }

        // Filter by classroom type
        if (selectedClassroomTypes.length > 0) {
            filtered = filtered.filter(classroom => selectedClassroomTypes.includes(classroom.classroom_type));
        }

        return filtered;
    }, [classrooms, searchTerm, selectedClassroomTypes]);

    const handleCreate = () => {
        form
            .validateFields()
            .then(values => {
                const payload = {
                    ...values,
                    school_id: schoolId,
                    id: singleClassroom?.id || undefined,
                };
                dispatch(createOrUpdateSchoolClassroom(payload));
                setIsModalVisible(false);
                form.resetFields();
                dispatch(resetSingleClassroom());
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleEdit = (id: number) => {
        dispatch(loadSingleClassroom(id));
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        dispatch(resetSingleClassroom());
    };

    const handleFilterCancel = () => {
        setIsFilterModalVisible(false);
    };

    const handleFilterApply = () => {
        setIsFilterModalVisible(false);
    };

    const handleFilterReset = () => {
        setSelectedClassroomTypes([]);
    };

    useEffect(() => {
        if (singleClassroom && isModalVisible) {
            form.setFieldsValue(singleClassroom);
        }
    }, [singleClassroom, isModalVisible, form]);

    // Debounced search handler
    const debouncedSearch = debounce((value: string) => {
        setSearchTerm(value);
    }, 300);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        debouncedSearch(value);
    };

    const columns: ColumnsType<SchoolClassroom> = [
        {
            title: 'Pavadinimas',
            dataIndex: 'title',
            key: 'title',
            sorter: (a: SchoolClassroom, b: SchoolClassroom) => a.title.localeCompare(b.title),
        },
        {
            title: 'Klasės tipas',
            dataIndex: 'classroom_type',
            key: 'classroom_type',
            render: (type: number) => {
                const option = classroomTypeOptions.find(opt => opt.value === type);
                return option ? <Tag color="blue">{option.label}</Tag> : type;
            },
        },
        {
            title: 'Fizinis vaikų skaičius iš viso',
            dataIndex: 'children_count',
            key: 'children_count',
            sorter: (a: SchoolClassroom, b: SchoolClassroom) => a.children_count - b.children_count,
        },
        {
            title: 'Bendras vaikų skaičius',
            dataIndex: 'total_children',
            key: 'total_children',
            sorter: (a: SchoolClassroom, b: SchoolClassroom) => a.total_children - b.total_children,
        },
        {
            title: 'Ukrainiečių vaikų skaičius',
            dataIndex: 'ukrainian_children_count',
            key: 'ukrainian_children_count',
            sorter: (a: SchoolClassroom, b: SchoolClassroom) => a.ukrainian_children_count - b.ukrainian_children_count,
        },
        {
            title: 'Užsieniečių vaikų skaičius',
            dataIndex: 'foreign_children_count',
            key: 'foreign_children_count',
            sorter: (a: SchoolClassroom, b: SchoolClassroom) => a.foreign_children_count - b.foreign_children_count,
        },
        {
            title: 'Diasporos vaikų skaičius',
            dataIndex: 'diaspora_children_count',
            key: 'diaspora_children_count',
            sorter: (a: SchoolClassroom, b: SchoolClassroom) => a.diaspora_children_count - b.diaspora_children_count,
        },
        {
            title: 'SUP vaikų skaičius',
            dataIndex: 'sup_children_count',
            key: 'sup_children_count',
            sorter: (a: SchoolClassroom, b: SchoolClassroom) => a.sup_children_count - b.sup_children_count,
        },
        {
            title: 'Veiksmai',
            key: 'actions',
            render: (text: string, record: SchoolClassroom) => (
                <Space size="middle">
                    <Tooltip title="Redaguoti klasę">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => record.id && handleEdit(record.id)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="school-classrooms">
            <Tabs defaultActiveKey="data-entry">
                <TabPane tab="Duomenų suvedimas" key="data-entry">
                    <Card>
                        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                            <Col>
                                <Input
                                    placeholder="Ieškoti klasių..."
                                    prefix={<SearchOutlined />}
                                    onChange={handleSearch}
                                    style={{ width: 300 }} />
                            </Col>
                            <Col>
                                <Space>
                                    <Button
                                        icon={<FilterOutlined />}
                                        onClick={() => setIsFilterModalVisible(true)}
                                        type={selectedClassroomTypes.length > 0 ? 'primary' : 'default'}>
                                        Filtrai
                                        {selectedClassroomTypes.length > 0 && ` (${selectedClassroomTypes.length})`}
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<PlusCircleOutlined />}
                                        onClick={() => setIsModalVisible(true)}>
                                        Pridėti klasę
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <Table
                            columns={columns}
                            dataSource={filteredClassrooms}
                            rowKey="id"
                            loading={isFetching}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} iš ${total} klasių`,
                            }} />
                    </Card>
                </TabPane>
                <TabPane tab="Statistika" key="statistics">
                    <SchoolClassroomStatistic schoolId={schoolId} />
                </TabPane>
            </Tabs>

            {/* Add/Edit Classroom Modal */}
            <Modal
                title={singleClassroom?.id ? 'Redaguoti klasę' : 'Pridėti naują klasę'}
                visible={isModalVisible}
                onOk={handleCreate}
                onCancel={handleCancel}
                okText={singleClassroom?.id ? 'Atnaujinti' : 'Pridėti'}
                cancelText="Atšaukti"
                width={600}>
                <Form layout="vertical" form={form}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Pavadinimas"
                                name="title"
                                rules={[
                                    { required: true, message: 'Įveskite klasės pavadinimą' },
                                    { max: 255, message: 'Pavadinimas negali viršyti 255 simbolių' },
                                ]}>
                                <Input placeholder="Pvz.: 1a klasė" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Klasės tipas"
                                name="classroom_type"
                                rules={[
                                    { required: true, message: 'Pasirinkite klasės tipą' },
                                ]}>
                                <Select placeholder="Pasirinkite klasės tipą">
                                    {classroomTypeOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Fizinis vaikų skaičius iš viso"
                                name="children_count"
                                rules={[
                                    { required: true, message: 'Įveskite vaikų skaičių iš viso' },
                                    { type: 'number', min: 0, message: 'Vaikų skaičius turi būti teigiamas' },
                                ]}>
                                <InputNumber
                                    placeholder="0"
                                    min={0}
                                    style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <div style={{ marginBottom: 16 }}>
                        <h4 style={{ margin: 0, color: '#666' }}>
                            Iš jų:
                        </h4>
                    </div>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Ukrainiečių vaikų skaičius"
                                name="ukrainian_children_count"
                                rules={[
                                    { required: true, message: 'Įveskite ukrainiečių vaikų skaičių' },
                                    { type: 'number', min: 0, message: 'Vaikų skaičius turi būti teigiamas' },
                                ]}>
                                <InputNumber
                                    placeholder="0"
                                    min={0}
                                    style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Kitų užsieniečių vaikų skaičius"
                                name="foreign_children_count"
                                rules={[
                                    { required: true, message: 'Įveskite užsieniečių vaikų skaičių' },
                                    { type: 'number', min: 0, message: 'Vaikų skaičius turi būti teigiamas' },
                                ]}>
                                <InputNumber
                                    placeholder="0"
                                    min={0}
                                    style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Diasporos vaikų skaičius"
                                name="diaspora_children_count"
                                rules={[
                                    { required: true, message: 'Įveskite diasporos vaikų skaičių' },
                                    { type: 'number', min: 0, message: 'Vaikų skaičius turi būti teigiamas' },
                                ]}>
                                <InputNumber
                                    placeholder="0"
                                    min={0}
                                    style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={(
                                    <span>
                                        SUP vaikų skaičius
                                        <Tooltip title="Mokiniai, pažymėti kaip turintys didelius arba
                                        labai didelius SUP, sistemoje skaičiuojami dvigubai. Tai reiškia,
                                        kad jeigu klasėje yra 20 mokinių, iš kurių vienas turi SUP,
                                        sistema bendram klasės mokinių skaičiui priskaičiuos 21.
                                        Fizinis mokinių skaičius išlieka 20, tačiau sistemoje jis
                                        bus apskaičiuojamas padidintas. Kaip turintys didelius arba
                                        labai didelius SUP,">
                                            <span style={{ marginLeft: 4, cursor: 'help' }}>
                                                <QuestionCircleOutlined style={{ }} />
                                            </span>
                                        </Tooltip>
                                    </span>
                                )}
                                name="sup_children_count"
                                rules={[
                                    { required: true, message: 'Įveskite SUP vaikų skaičių' },
                                    { type: 'number', min: 0, message: 'Vaikų skaičius turi būti teigiamas' },
                                ]}>
                                <InputNumber
                                    placeholder="0"
                                    min={0}
                                    style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Filter Modal */}
            <Modal
                title="Filtrai"
                visible={isFilterModalVisible}
                onOk={handleFilterApply}
                onCancel={handleFilterCancel}
                okText="Taikyti"
                cancelText="Atšaukti"
                width={400}>
                <div style={{ marginBottom: 16 }}>
                    <h4>Klasės tipas</h4>
                    <Select
                        mode="multiple"
                        placeholder="Pasirinkite klasės tipus"
                        value={selectedClassroomTypes}
                        onChange={setSelectedClassroomTypes}
                        style={{ width: '100%' }}
                        allowClear
                        maxTagCount="responsive">
                        {classroomTypeOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={handleFilterReset} style={{ marginRight: 8 }}>
                        Išvalyti
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default SchoolClassrooms;
